import { sourceControlToggleAtom } from '@/atoms/sourceControlAtoms';;
import { useAvailableIntegrations } from '@/components/chat/hooks/useAvailableIntegrations';
import { useCollections } from '@heidi/evidence/data';
import { useSourceLists } from '@heidi/evidence/data';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import {
  buildSelectedLibrarySources,
  buildSourceList,
  countDisplayedSourceTotal,
  countLibraryCollectionSources,
  summariseSourceSelection,
  type PersonalisedSource,
  type SourceSelectionSummary,
} from './personalised-source-avatars.utils';

const EMPTY_SUMMARY: SourceSelectionSummary = {
  partnerCount: 0,
  activeTypes: [],
  typeCount: 0,
  total: 0,
  isDefault: true,
};

export interface PersonalisedSourceSummary {
  summary: SourceSelectionSummary;
  selectionTotal: number;
  librarySources: PersonalisedSource[];
  partnerSources: PersonalisedSource[];
  defaultSources: PersonalisedSource[];
  libraryCount: number;
}

/** Shared selection summary for the personalised Sources button face + tooltip. */
export const usePersonalisedSourceSummary = (
  enabled = true,
  variant: 'evidence' | 'work' = 'evidence'
): PersonalisedSourceSummary => {
  const { partners } = useAvailableIntegrations();
  const { data: sourceLists } = useSourceLists();
  const { data: collections } = useCollections({ enabled });
  const toggleState = useAtomValue(sourceControlToggleAtom);

  const summary = useMemo(
    () =>
      enabled
        ? summariseSourceSelection(
            toggleState,
            partners.map((p) => p.id)
          )
        : EMPTY_SUMMARY,
    [enabled, toggleState, partners]
  );

  const partnerSources = useMemo<PersonalisedSource[]>(
    () =>
      enabled
        ? buildSourceList(partners, undefined, undefined, {
            partnerOk: (id) => toggleState.integrationPartners[id] === true,
            listOk: () => false,
            collectionOk: () => false,
          })
        : [],
    [enabled, partners, toggleState]
  );

  const defaultSources = useMemo<PersonalisedSource[]>(
    () =>
      enabled ? buildSourceList(partners, sourceLists, collections, null) : [],
    [enabled, partners, sourceLists, collections]
  );

  const librarySources = useMemo<PersonalisedSource[]>(
    () =>
      enabled
        ? buildSelectedLibrarySources(toggleState, sourceLists, collections)
        : [],
    [enabled, toggleState, sourceLists, collections]
  );

  const libraryCount = useMemo(
    () =>
      enabled
        ? countLibraryCollectionSources(
            toggleState,
            sourceLists,
            collections,
            variant
          )
        : 0,
    [enabled, toggleState, sourceLists, collections, variant]
  );

  const selectionTotal = enabled
    ? countDisplayedSourceTotal(
        summary,
        librarySources,
        libraryCount,
        variant
      )
    : 0;

  return {
    summary,
    selectionTotal,
    librarySources,
    partnerSources,
    defaultSources,
    libraryCount,
  };
};
